import type { RefreshTokenRequest } from '@/features/auth';
import { calculateTokenExpiry } from '@/lib/utils';
import type { IAuthService, IAuthSessionStore, ITokenRefreshService, StoredSessionData } from './types';
import type { ILogger } from '../log/types';
import { parseCognitoIdToken } from './cognito-id-token';

export interface TokenRefreshConfig {
  sessionStore: IAuthSessionStore;
  authService: IAuthService;
  logger: ILogger;
}

export class TokenRefreshService implements ITokenRefreshService {
  private refreshPromise: Promise<StoredSessionData | null> | null = null;
  private readonly listeners = new Set<(session: StoredSessionData) => void>();

  private readonly config: TokenRefreshConfig;
  private readonly logger: ILogger;

  constructor(config: TokenRefreshConfig) {
    this.config = config;
    this.logger = config.logger;
  }

  subscribe(listener: (session: StoredSessionData) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Main entry point:
   * - Check if token exists and is still valid (with a skew)
   * - If close to expiry or expired, try to refresh
   */
  async ensureValidToken(): Promise<StoredSessionData | null> {
    const storedSession = this.config.sessionStore.loadTokens();
    const { accessToken, expiresAt, refreshToken, userId } = storedSession;

    this.logger.debug('[TokenRefreshService] ensureValidToken - loaded session:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasUserId: !!userId,
      expiresAt,
    });

    if (!accessToken || !expiresAt) {
      this.logger.warn('[TokenRefreshService] No access token or expiresAt found in storage');
      return null;
    }

    if (!userId) {
      this.logger.error('[TokenRefreshService] No user ID associated with the session');
      return null;
    }

    const now = Date.now();
    const skewMs = 5 * 60 * 1000; // 5 minutes before expiry
    const timeUntilExpiry = expiresAt - now;

    // Token is still valid and not expiring soon
    if (timeUntilExpiry > skewMs) {
      return storedSession;
    }

    this.logger.info('[TokenRefreshService] Access token is expired or near expiry, attempting refresh');

    // Need to refresh, but no refresh token → logout
    if (!refreshToken) {
      this.logger.error('[TokenRefreshService] No refresh token available');
      // store.dispatch(logout());
      return null;
    }

    // Attempt refresh
    const refreshedSession = await this.refreshTokens(refreshToken, storedSession);
    if (!refreshedSession) {
      // Refresh failed → logout
      this.logger.error('[TokenRefreshService] Token refresh failed, logging out');
      return null;
    }

    this.config.sessionStore.saveTokens(refreshedSession);
    this.listeners.forEach((listener) => listener(refreshedSession));
    return refreshedSession;
  }

  /**
   * Prevent concurrent refresh attempts by sharing the same promise.
   */
  private async refreshTokens(refreshToken: string, oldSession: StoredSessionData): Promise<StoredSessionData | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh(refreshToken, oldSession);

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Actual refresh logic (calls AuthService.refreshToken).
   * Returns true if refresh succeeded, false otherwise.
   */
  private async performRefresh(refreshToken: string, oldSession: StoredSessionData): Promise<StoredSessionData | null> {
    try {
      this.logger.info('[TokenRefreshService] Performing token refresh ..');

      const authService = this.config.authService;
      if (!authService) {
        this.logger.error('[TokenRefreshService] Auth service not configured');
        return null;
      }

      if (!authService.refreshToken) {
        this.logger.error('[TokenRefreshService] Auth service does not support token refresh');
        return null;
      }

      const request: RefreshTokenRequest = { refreshToken };
      const response = await authService.refreshToken(request);

      if (!response.success || !response.tokens) {
        this.logger.error('[TokenRefreshService] Token refresh failed:', response.message);
        // store.dispatch(logout());
        return null;
      }

      // Calculate new expiry
      const expiresAt = calculateTokenExpiry(response.tokens.expiresIn);
      const { authRole, authUserType } = parseCognitoIdToken(response.tokens.idToken);

      return {
        ...oldSession,
        accessToken: response.tokens.accessToken,
        refreshToken: refreshToken,
        idToken: response.tokens.idToken,
        expiresAt: expiresAt,
        authRole,
        authUserType,
      };
    } catch (error) {
      this.logger.error('[TokenRefreshService] Error refreshing token:', error);
      // store.dispatch(logout());
      return null;
    }
  }
}
